/**
 * Lightweight subscription store (doc 147 §8.3 — "React 19 hooks +
 * use-sync-external-store; no Redux/Zustand").
 *
 * `createStore` is a tiny generic store consumed via `useSyncExternalStore`.
 * The app-wide store and its `useAppStore` hook live here too.
 */
import { useSyncExternalStore } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import type { Route } from './hashRouter.js';
import type { DensityMode } from '../theme/cssVariables.js';
import type { GraphContrast } from '../theme/tokens.js';
import type { ModeSetting } from './appearance.js';
import type { LayoutCoords } from '../graph/buildGraph.js';
import type { Board, Sandbox } from './atlasApiTypes.js';
import type { TrailEntry } from './workflowTrail.js';

/** 2D Sigma or 3D R3F constellation — one literal, used everywhere. */
export type ViewMode = '2d' | '3d';

export interface Store<T> {
  getSnapshot: () => T;
  subscribe: (listener: () => void) => () => void;
  setState: (patch: Partial<T> | ((prev: T) => Partial<T>)) => void;
}

/** Create a minimal observable store. */
export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setState(patch) {
      const next = typeof patch === 'function' ? patch(state) : patch;
      state = { ...state, ...next };
      for (const listener of listeners) listener();
    },
  };
}

// ── App-wide state ──────────────────────────────────────────────────────────

export type AsyncStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface InternalUser {
  uid: string;
  email: string;
  displayName: string;
  role: 'engineer' | 'designer' | 'admin';
}

export interface AppState {
  /** Current hash route. */
  route: Route;
  /** Loaded manifest, once `manifestStatus === 'ready'`. */
  manifest: AtlasManifest | null;
  manifestStatus: AsyncStatus;
  manifestError: string | null;
  /** Pre-computed constellation layout coords; `{}` until/unless fetched. */
  layout: LayoutCoords;
  /** Signed-in internal user, or null. */
  user: InternalUser | null;
  authStatus: AsyncStatus;
  /** True after `onAuthStateChanged(null)` fires on a previously-signed-in tab. */
  sessionExpired: boolean;
  /** ⌘K search palette open state. */
  searchOpen: boolean;
  /** Constellation view mode — 2D (Sigma) or 3D (R3F + three.js). */
  view: ViewMode;
  /** Active display density. */
  density: DensityMode;
  /** Mode setting (tri-state: auto follows OS, dark/light explicit). */
  mode: ModeSetting;
  /** Graph rendering contrast level (Standard / High). */
  graphContrast: GraphContrast;
  /** Resolved system pref last seen (read-only, refreshed by matchMedia). */
  systemPrefersDark: boolean;
  /** Recently-viewed entity ids, most-recent first. */
  recents: string[];
  /** Pinned entity ids. */
  pinned: string[];
  /** Session-only analysis breadcrumb of `{kind, id}` pairs the user has walked. */
  entityTrail: TrailEntry[];
  /** The user's saved boards (Phase 4). */
  boards: Board[];
  /** The user's What-If schema sandboxes (Phase 4 backend, Phase 7 UI). */
  sandboxes: Sandbox[];
  /** Admin-only: under-review sandboxes across all users (Phase 4 Task 10). */
  adminReviewQueue: Array<Sandbox & { ownerUid: string }>;
  /** Currently selected entity id for cross-view highlighting; preserved across view changes. */
  activeEntityId: string | null;
  /** Currently selected domain id for cross-view context; preserved across view changes. */
  activeDomainId: string | null;
  /** Currently active layer filter for cross-view navigation; preserved across view changes. */
  activeLayerFilter: string | null;
}

export const initialAppState: AppState = {
  route: { kind: 'welcome' },
  manifest: null,
  manifestStatus: 'idle',
  manifestError: null,
  layout: {},
  user: null,
  authStatus: 'idle',
  sessionExpired: false,
  searchOpen: false,
  view: '2d',
  density: 'compact',
  mode: 'auto',
  graphContrast: 'standard',
  systemPrefersDark: true,
  recents: [],
  pinned: [],
  entityTrail: [],
  boards: [],
  sandboxes: [],
  adminReviewQueue: [],
  activeEntityId: null,
  activeDomainId: null,
  activeLayerFilter: null,
};

export const appStore: Store<AppState> = createStore<AppState>(initialAppState);

/** Subscribe a component to the whole app state. */
export function useAppStore(): AppState {
  return useSyncExternalStore(appStore.subscribe, appStore.getSnapshot, appStore.getSnapshot);
}
